import React, { useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import {
    Alert,
    Box,
    Button,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Typography,
} from "@mui/material";
import Dialog from "../../General/ConfirmableDialog";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { INCIDENT_TYPE_LABEL_MAP } from "../../../data/Fleet/IncidentType";
import { handleViewFile } from "../../../api/handleViewFile";

interface RepairFile {
    id: string;
    fileName?: string;
    type?: string;
    fileType?: string;
}

interface RepairShopIncidentHistory {
    incidentId: string;
    incidentDate: string;
    assetId?: string | null;
    assetType?: string | null;
    unitNumber?: string | null;
    incidentType: string;
    description: string;
    status: string;
    totalRepairCost: number;
    completedDate?: string | null;
    files: RepairFile[];
}

interface PagedResult<T> {
    totalCount: number;
    items: T[];
}

interface Props {
    shop: { id: string; shopName: string } | null;
    onClose: () => void;
}

const formatLabel = (value?: string | null) =>
    value
        ? value.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase())
        : "—";

const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString() : "—";

const RepairShopIncidentHistoryDialog: React.FC<Props> = ({ shop, onClose }) => {
    const { token } = useAuth();
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 20,
    });

    const { data, isLoading, isError, error } = useQuery<
        PagedResult<RepairShopIncidentHistory>
    >({
        queryKey: [
            "repairShopIncidentHistory",
            shop?.id,
            paginationModel.page,
            paginationModel.pageSize,
        ],
        queryFn: async () => {
            const response = await axios.get(
                `${apiVariable}/repairShop/${shop!.id}/incidents`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        pageNumber: paginationModel.page + 1,
                        pageSize: paginationModel.pageSize,
                    },
                },
            );
            return response.data;
        },
        enabled: Boolean(shop?.id && token),
    });

    const columns: GridColDef<RepairShopIncidentHistory>[] = [
        {
            field: "incidentDate",
            headerName: "Incident date",
            width: 125,
            valueFormatter: (value) => formatDate(value),
        },
        {
            field: "unitNumber",
            headerName: "Asset / unit",
            width: 150,
            valueGetter: (_value, row) =>
                [row.assetType, row.unitNumber].filter(Boolean).join(" · ") || "—",
        },
        {
            field: "incidentType",
            headerName: "Type / category",
            width: 180,
            valueFormatter: (value) => INCIDENT_TYPE_LABEL_MAP[value] ?? formatLabel(value),
        },
        { field: "description", headerName: "Description", minWidth: 240, flex: 1 },
        {
            field: "status",
            headerName: "Status",
            width: 150,
            renderCell: ({ value }) => (
                <Chip
                    size="small"
                    label={formatLabel(value)}
                    color={value?.toLowerCase() === "completed" ? "success" : "default"}
                />
            ),
        },
        {
            field: "totalRepairCost",
            headerName: "Total cost",
            width: 130,
            align: "right",
            headerAlign: "right",
            valueFormatter: (value) =>
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(Number(value) || 0),
        },
        {
            field: "files",
            headerName: "Invoice / receipt",
            width: 190,
            sortable: false,
            filterable: false,
            renderCell: ({ value }) =>
                value?.length ? (
                    <Box sx={{ display: "flex", gap: 1, overflow: "auto" }}>
                        {value.map((file: RepairFile, index: number) => (
                            <Link
                                key={file.id ?? index}
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={() =>
                                    handleViewFile(
                                        file,
                                        (file.fileType ?? file.type ?? "invoice").toLowerCase(),
                                        token,
                                    )
                                }
                            >
                                {file.fileName || `File ${index + 1}`}
                            </Link>
                        ))}
                    </Box>
                ) : (
                    "—"
                ),
        },
    ];

    const handleClose = () => {
        setPaginationModel({ page: 0, pageSize: 20 });
        onClose();
    };

    return (
        <Dialog open={Boolean(shop)} onClose={handleClose} fullWidth maxWidth="xl">
            <DialogTitle>Incident history — {shop?.shopName}</DialogTitle>
            <DialogContent dividers>
                {isError ? (
                    <Alert severity="error">
                        {axios.isAxiosError(error)
                            ? error.response?.data || "Unable to load incident history."
                            : "Unable to load incident history."}
                    </Alert>
                ) : !isLoading && data?.items.length === 0 ? (
                    <Box sx={{ py: 8, textAlign: "center" }}>
                        <Typography variant="h6">No incidents found</Typography>
                        <Typography color="text.secondary">
                            This repair shop has no linked incidents.
                        </Typography>
                    </Box>
                ) : (
                    <DataGrid
                        autoHeight
                        rows={data?.items ?? []}
                        columns={columns}
                        getRowId={(row) => row.incidentId}
                        loading={isLoading}
                        paginationMode="server"
                        rowCount={data?.totalCount ?? 0}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[10, 20, 50]}
                        disableRowSelectionOnClick
                        sx={{ minHeight: 300 }}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RepairShopIncidentHistoryDialog;